// AIAccuracyPanel — top-of-page KPI strip + lightweight SVG trend chart +
// Weekly Digest preview modal + local digest-history drawer.
// Moved verbatim from DecisionEnginePage.js during Phase 2 of the System
// Consolidation. Admin can toggle between "All" and "Mine" scope; Staff is
// locked to "mine". KPI / TrendChart / TrendStreakPill sub-components are
// only used inside this panel so they live with it.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Check,
  ChevronDown,
  Copy,
  History,
  Loader2,
  Mail,
  Minus,
  Share2,
  Trash2,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';

import {
  API,
  DIGEST_HISTORY_KEY,
  computeFollowedStreak,
  readDigestHistory,
  relativeTime,
  writeDigestHistory,
} from './helpers';

// -- Sub-components used only by AIAccuracyPanel --------------------------

// Small KPI card — single metric block on the top row.
const KPI = ({ label, value, tone = 'indigo', isRaw = false, testId }) => {
  const tones = {
    emerald: 'text-emerald-600',
    indigo: 'text-indigo-600',
    amber: 'text-amber-600',
    gray: 'text-gray-500',
    slate: 'text-gray-900 dark:text-gray-100',
  };
  return (
    <div className="text-center rounded-md border border-gray-100 dark:border-gray-800 py-2" data-testid={testId}>
      <div className={`text-lg font-bold ${tones[tone]}`}>{isRaw ? value : `${value}%`}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
    </div>
  );
};

// Lightweight SVG bar chart — decisions (gray) / followed (emerald) /
// positive (indigo) per day.
const TrendChart = ({ trend }) => {
  if (!trend || trend.length === 0) return null;
  const W = 640, H = 110, P = 16;
  const max = Math.max(1, ...trend.map((t) => t.decisions));
  const innerW = W - P * 2;
  const innerH = H - P * 2;
  const slotW = innerW / trend.length;
  const barW = Math.max(2, Math.min(16, slotW / 4));
  const yFor = (v) => P + innerH - (v / max) * innerH;

  return (
    <div className="overflow-x-auto" data-testid="ai-accuracy-trend">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28">
        {/* baseline */}
        <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="1" />
        {trend.map((t, i) => {
          const xCenter = P + slotW * i + slotW / 2;
          return (
            <g key={t.date}>
              <rect
                x={xCenter - barW * 1.5}
                y={yFor(t.decisions)}
                width={barW}
                height={Math.max(1, (t.decisions / max) * innerH)}
                className="fill-gray-300 dark:fill-gray-600"
              />
              <rect
                x={xCenter - barW / 2}
                y={yFor(t.followed)}
                width={barW}
                height={Math.max(1, (t.followed / max) * innerH)}
                className="fill-emerald-500"
              />
              <rect
                x={xCenter + barW / 2}
                y={yFor(t.positive)}
                width={barW}
                height={Math.max(1, (t.positive / max) * innerH)}
                className="fill-indigo-500"
              />
              {i === 0 || i === trend.length - 1 ? (
                <text x={xCenter} y={H - 2} textAnchor="middle" className="fill-gray-400 text-[8px]">
                  {t.date.slice(5)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <div className="flex items-center justify-center gap-3 text-[10px] text-gray-500 mt-0.5">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-gray-300 dark:bg-gray-600" /> decisions</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" /> followed</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-indigo-500" /> positive</span>
      </div>
    </div>
  );
};

// Trend Streak pill — direction indicator derived from the local digest
// history array.
const TrendStreakPill = ({ history }) => {
  const { kind, weeks } = computeFollowedStreak(history);
  if (kind === 'up') {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 px-2.5 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400"
        data-testid="trend-streak-pill"
        data-streak-kind="up"
      >
        <TrendingUp className="h-3 w-3" />
        <span>{weeks} week{weeks === 1 ? '' : 's'} rising Followed%</span>
      </div>
    );
  }
  if (kind === 'down') {
    return (
      <div
        className="inline-flex items-center gap-1.5 rounded-full bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 px-2.5 py-0.5 text-[11px] text-red-700 dark:text-red-400"
        data-testid="trend-streak-pill"
        data-streak-kind="down"
      >
        <TrendingDown className="h-3 w-3" />
        <span>{weeks} week{weeks === 1 ? '' : 's'} declining Followed%</span>
      </div>
    );
  }
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 px-2.5 py-0.5 text-[11px] text-gray-500"
      data-testid="trend-streak-pill"
      data-streak-kind="none"
    >
      <Minus className="h-3 w-3" />
      <span>No clear trend</span>
    </div>
  );
};

// -- Main panel -----------------------------------------------------------

export const AIAccuracyPanel = ({ user }) => {
  const [days, setDays] = useState(30);
  const [scope, setScope] = useState('all'); // 'all' | 'mine' — Admin-only toggle
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestCopied, setDigestCopied] = useState(false);
  const [digestMdCopied, setDigestMdCopied] = useState(false);
  const [digestShared, setDigestShared] = useState(false);
  const [history, setHistory] = useState(() => readDigestHistory());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isHistoricalView, setIsHistoricalView] = useState(false);

  const isAdmin = user?.role === 'Admin';

  const openDigest = async () => {
    setDigestOpen(true);
    setDigest(null);
    setIsHistoricalView(false);
    setDigestLoading(true);
    try {
      const params = {};
      if (isAdmin && scope === 'mine' && user?.id) params.coach_id = user.id;
      const r = await axios.get(`${API}/intelligence/metrics/weekly-digest`, { params });
      setDigest(r.data);
      // Persist only live fetches — historical reopens don't re-save.
      setHistory(writeDigestHistory(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load weekly digest');
      setDigestOpen(false);
    } finally {
      setDigestLoading(false);
    }
  };

  const openHistoricalDigest = (entry) => {
    if (!entry?.snapshot) return;
    setIsHistoricalView(true);
    setDigest(entry.snapshot);
    setDigestLoading(false);
    setDigestOpen(true);
  };

  const clearDigestHistory = () => {
    localStorage.removeItem(DIGEST_HISTORY_KEY);
    setHistory([]);
    toast.success('History cleared');
  };

  const copyDigest = async () => {
    if (!digest?.body_text) return;
    try {
      await navigator.clipboard.writeText(`Subject: ${digest.subject}\n\n${digest.body_text}`);
      setDigestCopied(true);
      toast.success('Digest copied');
      setTimeout(() => setDigestCopied(false), 1800);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const buildDigestMarkdown = (d) => [
    '# PFAP AI Weekly Report',
    '',
    '## 📊 Summary',
    `- Total Decisions: ${d.total_decisions}`,
    `- Followed Rate: ${d.followed_rate}%`,
    `- Positive Outcome Rate: ${d.positive_outcome_rate}%`,
    '',
    '## 🧠 Insight',
    d.top_insight,
    '',
    '## ⭐ Highlight',
    d.key_highlight,
    '',
    '## 📅 Period',
    'Last 7 days',
    '',
  ].join('\n');

  const writeClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  const copyDigestMarkdown = async () => {
    if (!digest) return;
    const md = buildDigestMarkdown(digest);
    try {
      await writeClipboard(md);
      setDigestMdCopied(true);
      toast.success('Copied Markdown');
      setTimeout(() => setDigestMdCopied(false), 1800);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const shareDigest = async () => {
    if (!digest) return;
    const md = buildDigestMarkdown(digest);
    // Prefer native system share if available (mobile + some desktop browsers).
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'PFAP AI Weekly Report',
          text: md,
        });
        setDigestShared(true);
        toast.success('Shared');
        setTimeout(() => setDigestShared(false), 1800);
        return;
      } catch (e) {
        // User may have aborted the share sheet — only fall through on real error.
        if (e?.name === 'AbortError') return;
      }
    }
    // Fallback: clipboard.
    try {
      await writeClipboard(md);
      setDigestShared(true);
      toast.success('Copied for sharing ✓');
      setTimeout(() => setDigestShared(false), 1800);
    } catch (e) {
      toast.error('Share failed');
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = { days };
        if (isAdmin && scope === 'mine' && user?.id) {
          params.coach_id = user.id;
        }
        const r = await axios.get(`${API}/intelligence/metrics`, { params });
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || 'Metrics unavailable');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days, scope, isAdmin, user?.id]);

  if (collapsed) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="ai-accuracy-panel-collapsed">
        <Button variant="outline" size="sm" onClick={() => setCollapsed(false)} data-testid="ai-accuracy-expand">
          Show AI Accuracy
        </Button>
      </div>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto border-indigo-100 dark:border-indigo-900/40" data-testid="ai-accuracy-panel">
      <CardContent className="py-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Accuracy</h3>
            <p className="text-xs text-gray-500">
              Last {days} days · scope: <span className="font-medium">{data?.scope || '—'}</span>
              {data?.last_digest_sent_at && (
                <>
                  {' · '}
                  <span
                    className="text-gray-400"
                    title={new Date(data.last_digest_sent_at).toLocaleString()}
                    data-testid="last-digest-sent-badge"
                  >
                    last digest: {relativeTime(data.last_digest_sent_at)}
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <div className="flex items-center gap-1 border rounded-md p-0.5" data-testid="ai-accuracy-scope">
                {[
                  { k: 'all', label: 'All' },
                  { k: 'mine', label: 'Mine' },
                ].map((s) => (
                  <button
                    key={s.k}
                    type="button"
                    onClick={() => setScope(s.k)}
                    className={`px-2 py-0.5 text-xs rounded ${scope === s.k ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-800'}`}
                    data-testid={`scope-${s.k}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1 border rounded-md p-0.5" data-testid="ai-accuracy-days">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`px-2 py-0.5 text-xs rounded ${days === d ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-800'}`}
                  data-testid={`days-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={openDigest}
              data-testid="digest-preview-btn"
              title="Preview the weekly email digest"
            >
              <Mail className="h-3.5 w-3.5 mr-1" />
              Weekly Report
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setCollapsed(true)} data-testid="ai-accuracy-collapse">
              Hide
            </Button>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center gap-2 text-xs text-gray-400 py-4">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Calculating…
          </div>
        ) : !data || data.total_decisions === 0 ? (
          <div className="text-center py-6 text-xs text-gray-500" data-testid="ai-accuracy-empty">
            No decisions in this window yet. Save + rate a few from the engine to start tracking performance.
          </div>
        ) : (
          <>
            {/* KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="ai-accuracy-kpis">
              <KPI label="% Followed" value={data.followed_rate} tone="emerald" testId="kpi-followed" />
              <KPI label="% Positive" value={data.positive_outcome_rate} tone="indigo" testId="kpi-positive" />
              <KPI label="% Pending" value={data.pending_rate} tone="gray" testId="kpi-pending" />
              <KPI label="Total" value={data.total_decisions} isRaw tone="slate" testId="kpi-total" />
              <KPI label="% Ignored" value={data.ignored_rate} tone="amber" testId="kpi-ignored" />
            </div>

            {/* Secondary line */}
            <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="ai-accuracy-secondary">
              <span>Positive when followed: <span className="font-semibold text-gray-700 dark:text-gray-300">{data.followed_positive_rate}%</span></span>
              <span>Neutral: {data.neutral_outcome_rate}%</span>
              <span>Negative: {data.negative_outcome_rate}%</span>
            </div>

            {/* Trend chart */}
            <TrendChart trend={data.trend} />

            {/* Digest History drawer — local-only */}
            <div className="border-t pt-3 -mx-4 px-4" data-testid="digest-history-drawer">
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                className="w-full flex items-center justify-between text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                data-testid="digest-history-toggle"
              >
                <span className="inline-flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5" />
                  Digest History ({history.length})
                </span>
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${historyOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {historyOpen && (
                <div className="mt-2 space-y-1.5" data-testid="digest-history-list">
                  {/* Trend streak pill — derived only from the local history array. */}
                  <TrendStreakPill history={history} />
                  {history.length === 0 ? (
                    <div className="text-[11px] text-gray-400 italic py-2" data-testid="digest-history-empty">
                      No digests previewed yet. Open the Weekly Report to start building your history (stored locally, last 8).
                    </div>
                  ) : (
                    <>
                      {history.map((h, i) => {
                        const prev = history[i + 1];  // older entry = previous week
                        const fmtDelta = (curr, previous) => {
                          if (!previous) return null;
                          const c = Number.isFinite(curr) ? curr : 0;
                          const p = Number.isFinite(previous) ? previous : 0;
                          const d = Math.round((c - p) * 10) / 10;
                          const tone =
                            d > 0 ? 'text-emerald-600'
                            : d < 0 ? 'text-red-600'
                            : 'text-gray-400';
                          const sign = d > 0 ? '+' : (d < 0 ? '' : '');
                          return { text: `${sign}${d}%`, tone };
                        };
                        const df = fmtDelta(h.followed_rate, prev?.followed_rate);
                        const dp = fmtDelta(h.positive_outcome_rate, prev?.positive_outcome_rate);
                        return (
                          <button
                            key={h.id}
                            type="button"
                            onClick={() => openHistoricalDigest(h)}
                            className="w-full text-left rounded-md border border-gray-100 dark:border-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700 hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20 px-3 py-2 transition-colors"
                            data-testid={`digest-history-entry-${h.id}`}
                          >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="text-xs text-gray-500">{relativeTime(h.timestamp)}</div>
                              <div className="flex items-center gap-3 text-[11px]">
                                <span className="text-emerald-600 font-semibold inline-flex items-center gap-1">
                                  F {h.followed_rate}%
                                  <span className={`text-[10px] font-normal ${df ? df.tone : 'text-gray-400'}`} data-testid={`delta-followed-${h.id}`}>
                                    ({df ? df.text : '—'})
                                  </span>
                                </span>
                                <span className="text-indigo-600 font-semibold inline-flex items-center gap-1">
                                  + {h.positive_outcome_rate}%
                                  <span className={`text-[10px] font-normal ${dp ? dp.tone : 'text-gray-400'}`} data-testid={`delta-positive-${h.id}`}>
                                    ({dp ? dp.text : '—'})
                                  </span>
                                </span>
                                <span className="text-gray-400">n={h.total_decisions}</span>
                              </div>
                            </div>
                            {h.insight && (
                              <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5 truncate">
                                {h.insight}
                              </div>
                            )}
                          </button>
                        );
                      })}
                      <div className="flex justify-end pt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearDigestHistory}
                          className="h-6 px-2 text-[11px] text-gray-400 hover:text-red-600"
                          data-testid="digest-history-clear"
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Clear
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Weekly Digest Preview Modal */}
      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-lg" data-testid="digest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-indigo-600" />
              Weekly AI Report · Preview
              {isHistoricalView && (
                <Badge variant="outline" className="text-[10px] ml-1" data-testid="digest-historical-badge">
                  Historical snapshot
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Live preview of this week&apos;s AI Performance digest — KPIs, insight, and highlight. Email delivery is not wired yet.
            </DialogDescription>
          </DialogHeader>
          {digestLoading || !digest ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Generating digest…
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <div className="text-xs text-gray-500">
                Subject: <span className="font-medium text-gray-700 dark:text-gray-300" data-testid="digest-subject">{digest.subject}</span>
                <span className="ml-2">· scope: <span className="font-medium">{digest.scope}</span></span>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">This Week's AI Performance</div>
                <ul className="list-disc pl-5 space-y-0.5" data-testid="digest-kpis">
                  <li>Total Decisions: <span className="font-semibold">{digest.total_decisions}</span></li>
                  <li>Followed Rate: <span className="font-semibold">{digest.followed_rate}%</span></li>
                  <li>Positive Outcome Rate: <span className="font-semibold">{digest.positive_outcome_rate}%</span></li>
                </ul>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Insight</div>
                <p className="text-gray-800 dark:text-gray-200" data-testid="digest-insight">{digest.top_insight}</p>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Highlight</div>
                <p className="text-gray-700 dark:text-gray-300 italic" data-testid="digest-highlight">{digest.key_highlight}</p>
              </div>
              <div className="text-[10px] text-gray-400 border-t pt-2">
                Email delivery is not scheduled yet — this is a live preview.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={copyDigest}
              disabled={!digest || digestLoading}
              data-testid="digest-copy-btn"
            >
              {digestCopied ? (
                <><Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Copied</>
              ) : (
                <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={copyDigestMarkdown}
              disabled={!digest || digestLoading}
              data-testid="digest-copy-md-btn"
              title="Copy as Markdown for Slack / Notion"
            >
              {digestMdCopied ? (
                <><Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Copied Markdown</>
              ) : (
                <><Copy className="h-3.5 w-3.5 mr-1" /> Copy Markdown</>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={shareDigest}
              disabled={!digest || digestLoading}
              data-testid="digest-share-btn"
              title="Share via system share sheet (falls back to clipboard)"
            >
              {digestShared ? (
                <><Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Ready to paste</>
              ) : (
                <><Share2 className="h-3.5 w-3.5 mr-1" /> Share Digest</>
              )}
            </Button>
            <Button size="sm" onClick={() => setDigestOpen(false)} data-testid="digest-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
